import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console or error reporting service
    console.error("Error Boundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // You can also log the error to an error reporting service here
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise use default error page
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex justify-center items-center px-4 min-h-screen bg-neutral-50">
          <div className="p-8 w-full max-w-2xl bg-white rounded-2xl shadow-xl">
            <div className="text-center">
              <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-red-100 rounded-full">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h1 className="mb-2 text-3xl font-bold text-neutral-900">
                Something went wrong
              </h1>
              <p className="mb-6 text-neutral-600">
                We&apos;re sorry for the inconvenience. An unexpected error has
                occurred.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <div className="mb-6 text-left">
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <h3 className="mb-2 text-sm font-semibold text-red-900">
                      Error Details:
                    </h3>
                    <p className="mb-2 font-mono text-sm text-red-800">
                      {this.state.error.toString()}
                    </p>
                    {this.state.errorInfo && (
                      <details className="mt-2">
                        <summary className="text-sm font-medium text-red-900 cursor-pointer">
                          Component Stack
                        </summary>
                        <pre className="overflow-auto mt-2 max-h-64 text-xs text-red-800">
                          {this.state.errorInfo.componentStack}
                        </pre>
                      </details>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-3 justify-center sm:flex-row">
                <button
                  onClick={this.handleReset}
                  className="px-6 py-3 font-medium text-white rounded-lg transition-colors bg-primary-600 hover:bg-primary-700"
                >
                  Try Again
                </button>
                <button
                  onClick={() => (window.location.href = "/dashboard")}
                  className="px-6 py-3 font-medium rounded-lg transition-colors bg-neutral-200 text-neutral-900 hover:bg-neutral-300"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
